// AuthDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Tabs,
    Tab,
    Box,
    IconButton,
    InputAdornment,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
    Link,
    Typography,
    Chip,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import AuthService from '../Auth/AuthService';
import { useNavigate } from 'react-router-dom';
import { LogInIcon } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

const EMPTY_LOGIN = { username: '', password: '' };
const EMPTY_REGISTER = { email: '', mobile: '', password: '', confirmPassword: '' };

const AuthDialog = ({ open, onClose, onAuthSuccess, initialTab = 0 }) => {
    const [tab, setTab] = useState(initialTab);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [loginData, setLoginData] = useState(EMPTY_LOGIN);
    const [showLoginPassword, setShowLoginPassword] = useState(false);

    const [registerData, setRegisterData] = useState(EMPTY_REGISTER);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [manualSignup, setManualSignup] = useState(false);

    const navigate = useNavigate();

    // Result of /auth/check-email for the Login tab: null = not checked yet
    const [emailStatus, setEmailStatus] = useState(null); // { googleUser, normalUser } | null
    const [showContinueButton, setShowContinueButton] = useState(true);

    useEffect(() => {
        if (!open) resetForm();
    }, [open]);

    const resetForm = () => {
        setLoginData(EMPTY_LOGIN);
        setRegisterData(EMPTY_REGISTER);
        setError('');
        setEmailStatus(null);
        setShowContinueButton(true);
        setAcceptTerms(false);
        setManualSignup(false);
        setLoading(false);
    };

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
        setError('');
        setShowContinueButton(true);
        setEmailStatus(null);
        setAcceptTerms(false);
        setManualSignup(false);
        setLoginData(EMPTY_LOGIN);
        setRegisterData(EMPTY_REGISTER);
    };

    const handleLoginChange = (e) => {
        const { name, value } = e.target;
        setLoginData((prev) => ({ ...prev, [name]: value }));
        setError('');
        // Username changed after a check already ran — force a re-check.
        if (name === 'username' && emailStatus !== null) {
            setEmailStatus(null);
            setShowContinueButton(true);
        }
    };

    const handleRegisterChange = (e) => {
        setRegisterData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setError('');
    };

    const handleTermsChange = (e) => {
        setAcceptTerms(e.target.checked);
        setError('');
    };

    const validateManualRegister = () => {
        if (!registerData.email.trim()) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(registerData.email)) return 'Invalid email format';
        if (!registerData.mobile.trim()) return 'Mobile number is required';
        if (!/^\d{10}$/.test(registerData.mobile)) return 'Mobile must be 10 digits';
        if (!registerData.password) return 'Password is required';
        if (registerData.password.length < 6) return 'Password must be at least 6 characters';
        if (registerData.password !== registerData.confirmPassword) return 'Passwords do not match';
        if (!acceptTerms) return 'You must accept the Terms and Conditions to register';
        return null;
    };

    const routeAfterLogin = (tokendata) => {
        if (tokendata?.is_verified === true || tokendata?.roles === 'SUPPERADMIN') {
            navigate('/');
        } else if (tokendata?.is_verified === false) {
            navigate('/verify');
        } else if (tokendata?.accessToken !== undefined) {
            navigate('/loginview');
        }
        setTimeout(() => window.location.reload(), 100);
    };

    // Step 1 (Login tab): check whether this email belongs to a Google or password account
    const handleCheckEmail = async () => {
        if (!loginData.username.trim()) {
            setError('Please enter your email or username');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const rest = AuthService.pathUrl('POST', '/auth/check-email');
            const res = await AuthService.restPost(rest, { username: loginData.username });

            if (!res || (!res.googleUser && !res.normalUser)) {
                setEmailStatus({ googleUser: false, normalUser: false });
                setError('No account found with this email. Try registering, or use Google.');
                setTab(1);
                return;
            }

            setEmailStatus({ googleUser: !!res.googleUser, normalUser: !!res.normalUser });
            setShowContinueButton(false);

            if (res.googleUser) {
                setError('This email is registered with Google. Please sign in with Google.');
            } else if (res.normalUser) {
                setError(''); // clear — password field will just appear
            }
        } catch (err) {
            console.error('Email check failed:', err);
            setError('Unable to verify email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async (credentialResponse) => {
        setLoading(true);
        setError('');
        try {
            const decoded = jwtDecode(credentialResponse.credential);
            const rest = AuthService.pathUrl('POST', '/auth/google-login');
            const tokendata = await AuthService.registerLogin(rest, {
                username: decoded.email,
                // token: credentialResponse.credential,
            });

            if (tokendata) {
                routeAfterLogin(tokendata);
                // sessionStorage.setItem('token', tokendata.accessToken);
                // routeAfterLogin(AuthService.tokenInfo())
                onAuthSuccess({ name: decoded.name, email: decoded.email, role: tokendata?.roles });
                onClose();
            }
        } catch (err) {
            console.error('Google login error:', err);
            setError('Google login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignup = async (credentialResponse) => {
        if (!acceptTerms) {
            setError('Please accept the Terms & Conditions to continue.');
            return;
        }
        if (!/^\d{10}$/.test(registerData.mobile)) {
            setError('Please enter a valid 10-digit mobile number to continue.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const decoded = jwtDecode(credentialResponse.credential);
            const rest = AuthService.pathUrl('POST', '/auth/google-signup');
            const tokendata = await AuthService.registerLogin(rest, {
                email: decoded.email,
                mobile: registerData.mobile,
                roleType: 'ADMIN',
                loginType: 'SCHOOL',
                isActive: true,
                acceptTerms: true,
            });

            if (tokendata) {
                routeAfterLogin(tokendata);
                onAuthSuccess({ name: decoded.name, email: decoded.email, role: tokendata?.roles });
                onClose();
            }
        } catch (err) {
            console.error('Google signup error:', err);
            setError('Google signup failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleError = () => {
        setError('Google authentication failed. Please try again.');
        setLoading(false);
    };

    const handleLogin = async () => {
        if (!loginData.username.trim() || !loginData.password.trim()) {
            setError('Please enter both username and password');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const rest = AuthService.pathUrl('POST', '/auth/login');
            const tokendata = await AuthService.registerLogin(rest, loginData);

            if (tokendata) {
                routeAfterLogin(tokendata);
                onAuthSuccess({ username: loginData.username, role: tokendata?.roles });
                onClose();
            }
        } catch (err) {
            console.error('Login error:', err);
            setError(err.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async () => {
        const validationError = validateManualRegister();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const data = {
                email: registerData.email,
                mobile: registerData.mobile,
                password: registerData.password,
                roleType: 'ADMIN',
                loginType: 'SCHOOL',
                isActive: true,
                acceptTerms: true,
            };

            const rest = AuthService.pathUrl('POST', '/auth/register');
            const tokendata = await AuthService.registerLogin(rest, data);

            if (tokendata?.status === 404) {
                setError(tokendata.message || 'Registration failed');
                return;
            }

            if (tokendata) {
                routeAfterLogin(tokendata);
                onAuthSuccess({ email: registerData.email });
                onClose();
            }
        } catch (err) {
            console.error('Registration error:', err);
            setError(err.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const passwordFieldProps = (value, show, setShow, name, label) => ({
        margin: 'normal',
        required: true,
        fullWidth: true,
        label,
        name,
        type: show ? 'text' : 'password',
        value,
        onChange: handleRegisterChange,
        disabled: loading,
        slotProps: {
            input: {
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={() => setShow(!show)} edge="end">
                            {show ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                ),
            },
        },
    });

    const termsCheckbox = (
        <FormControlLabel
            control={<Checkbox checked={acceptTerms} onChange={handleTermsChange} disabled={loading} color="primary" />}
            label={
                <Typography variant="body2">
                    I agree to the{' '}
                    <Link href="/terms-conditions" target="_blank" rel="noopener" underline="hover" color="primary">
                        Terms and Conditions
                    </Link>
                </Typography>
            }
        />
    );

    // Existing-user Google sign-in
    const renderGoogleLogin = () => (
        <Box display="flex" justifyContent="center">
            <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={handleGoogleError}
                theme="filled_black"
                shape="pill"
                width="200"
                text="signin_with"
            />
        </Box>
    );

    // New-user Google sign-up (requires mobile + accepted terms first)
    const renderGoogleSignup = () => {
        const ready = /^\d{10}$/.test(registerData.mobile) && acceptTerms;
        if (!ready) {
            return (
                <Button disabled variant="outlined" fullWidth sx={{ borderRadius: 6 }}>
                    Enter valid 10-digit mobile number & accept terms to use Google
                </Button>
            );
        }
        return (
            <Box display="flex" justifyContent="center">
                <GoogleLogin
                    onSuccess={handleGoogleSignup}
                    onError={handleGoogleError}
                    theme="filled_black"
                    shape="pill"
                    width="200"
                    text="signup_with"
                />
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            slotProps={{
                paper: {
                    sx: {
                        border: '1px solid #515152',
                        borderRadius: '25px',
                    },
                },
            }}>
            <DialogTitle>
                <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
                    <Tab label="Login" icon={<LogInIcon />} iconPosition="start" />
                    <Tab label="Register" icon={<PersonAddIcon />} iconPosition="start" />
                </Tabs>
            </DialogTitle>

            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* ---------------- LOGIN TAB ---------------- */}
                {tab === 0 && (
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Username / Email"
                            name="username"
                            autoComplete="username"
                            autoFocus
                            value={loginData.username}
                            onChange={handleLoginChange}
                            disabled={loading}
                        />

                        {emailStatus?.normalUser && (
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="Password"
                                name="password"
                                autoFocus
                                type={showLoginPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                value={loginData.password}
                                onChange={handleLoginChange}
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowLoginPassword(!showLoginPassword)} edge="end">
                                                    {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        )}


                        <Box mt={2}>
                            <Typography variant="body2" sx={{ mb: 2, fontWeight: 900, display: "flex", justifyContent: "center" }}>
                                OR
                            </Typography>
                            {renderGoogleLogin()}</Box>

                    </Box>
                )}

                {/* ---------------- REGISTER TAB ---------------- */}
                {tab === 1 && (
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Mobile Number"
                            name="mobile"
                            autoComplete="tel"
                            value={registerData.mobile}
                            onChange={handleRegisterChange}
                            disabled={loading}
                            placeholder="Enter 10-digit mobile number"
                        />
                        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                            {termsCheckbox}
                            {/* <Chip onClick={() => setManualSignup(false)}
                                label={manualSignup ? 'True' : 'False'}
                                color={manualSignup ? 'success' : 'default'}
                                size="small"
                            /> */}
                        </Box>
                        {!manualSignup && (
                            <>
                                <Box mt={2}>{renderGoogleSignup()}</Box>
                                {/* <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                                    email & password?{' '}
                                    <Link component="button" type="button" onClick={() => setManualSignup(true)}>
                                        Sign up manually
                                    </Link>
                                </Typography> */}
                            </>
                        )}

                        {manualSignup && (
                            <>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    autoComplete="email"
                                    value={registerData.email}
                                    onChange={handleRegisterChange}
                                    disabled={loading}
                                />
                                <TextField {...passwordFieldProps(registerData.password, showRegisterPassword, setShowRegisterPassword, 'password', 'Password')} />
                                <TextField {...passwordFieldProps(registerData.confirmPassword, showConfirmPassword, setShowConfirmPassword, 'confirmPassword', 'Confirm Password')} />
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={() => { resetForm(); onClose(); }} disabled={loading}>
                    Cancel
                </Button>

                {/* Login tab: Continue -> check email, then Login once a password account is confirmed */}
                {tab === 0 && showContinueButton && (
                    <Button onClick={handleCheckEmail} disabled={loading || !loginData.username} variant="contained">
                        {loading ? <CircularProgress size={24} /> : 'Continue'}
                    </Button>
                )}
                {tab === 0 && !showContinueButton && emailStatus?.normalUser && (
                    <Button onClick={handleLogin} variant="contained" disabled={loading || !loginData.password}>
                        {loading ? <CircularProgress size={24} /> : 'Login'}
                    </Button>
                )}

                {/* Register tab: manual Register button only shown in manual mode */}
                {tab === 1 && manualSignup && (
                    <Button onClick={handleRegister} variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Register'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AuthDialog;